import { AsanaMCPSelector } from "@/components/EntitySelector/integrations/AsanaMCPSelector";
import { BaseSelectorProps } from "@/components/EntitySelector/integrations/BaseSelector";
import { GitHubSelector } from "@/components/EntitySelector/integrations/GitHubSelector";
import { GmailSelector } from "@/components/EntitySelector/integrations/GmailSelector";
import { GoogleCalendarSelector } from "@/components/EntitySelector/integrations/GoogleCalendarSelector";
import { DriveSelector } from "@/components/EntitySelector/integrations/GoogleDriveSelector";
import { GranolaMCPSelector } from "@/components/EntitySelector/integrations/GranolaMCPSelector";
import { HubSpotSelector } from "@/components/EntitySelector/integrations/HubspotSelector";
import { MicrosoftTeamsSelector } from "@/components/EntitySelector/integrations/MicrosoftTeamsSelector";
import { NotionMCPSelector } from "@/components/EntitySelector/integrations/NotionMCPSelector";
import { OutlookCalendarSelector } from "@/components/EntitySelector/integrations/OutlookCalendarSelector";
import { OutlookSelector } from "@/components/EntitySelector/integrations/OutlookSelector";
import { SlackSelector } from "@/components/EntitySelector/integrations/SlackSelector";
import { ZoomSelector } from "@/components/EntitySelector/integrations/ZoomSelector";
import React from "react";

/**
 * Centralized mapping of integration platforms to their entity selector components.
 * This is the single source of truth for which platforms have entity selectors implemented.
 *
 * Key: integration_platform value from the API
 * Value: React component for the entity selector
 */
export const PLATFORM_ENTITY_SELECTORS: Record<
  string,
  React.FC<BaseSelectorProps>
> = {
  // Microsoft platforms
  outlook: OutlookSelector,
  microsoft_outlook: OutlookSelector,
  microsoft_outlook_calendar: OutlookCalendarSelector,
  microsoft_teams: MicrosoftTeamsSelector,

  // Google platforms
  gmail: GmailSelector,
  google_mail: GmailSelector,
  google_drive: DriveSelector,
  google_calendar: GoogleCalendarSelector,

  // Other platforms
  asana: AsanaMCPSelector,
  asana_mcp: AsanaMCPSelector,
  slack: SlackSelector, // Slack connections use the `slack` platform (provider nango)
  slack_v2: SlackSelector,
  notion: NotionMCPSelector, // MCP version is now default
  notion_mcp: NotionMCPSelector, // Explicit MCP mapping
  hubspot: HubSpotSelector,
  zoom: ZoomSelector,
  github: GitHubSelector,
  granola: GranolaMCPSelector,
  granola_mcp: GranolaMCPSelector,
};

/**
 * Helper function to check if a platform has an entity selector implemented
 * @param platform - The integration_platform value from the API
 * @returns true if the platform has an entity selector, false otherwise
 */
export const hasPlatformEntitySelector = (platform: string): boolean => {
  return platform in PLATFORM_ENTITY_SELECTORS;
};

/**
 * Helper function to get the entity selector component for a platform
 * @param platform - The integration_platform value from the API
 * @returns The selector component or undefined if not found
 */
export const getPlatformEntitySelector = (platform: string) => {
  return PLATFORM_ENTITY_SELECTORS[platform];
};

/**
 * Get all supported platforms (for debugging/admin purposes)
 * @returns Array of platform strings that have entity selectors
 */
export const getSupportedPlatforms = (): string[] => {
  return Object.keys(PLATFORM_ENTITY_SELECTORS);
};
